/**
 * @fileoverview Modal "Meu Perfil" — o PRÓPRIO usuário edita seus dados
 * pessoais (avatar → Perfil). Escopo 100% pessoal para todos (admin incluso):
 * abas Dados (foto, nome, email, CPF, senha, cargo read-only) e Segurança
 * (MFA pessoal via MfaPersonalManager). Campos administrativos ficam no
 * UserEditorModal (página Usuários).
 *
 * Design espelhado 1:1 do UserEditorModal (casca, abas, campos, footer).
 * O payload do save envia APENAS os campos pessoais — o whitelist de self-edit
 * do backend (UserController.update) é a fonte da verdade do que persiste.
 */

import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import InputMask from "react-input-mask";
import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Avatar,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  Pencil,
  X,
  Upload,
  User as PersonIcon,
  ShieldCheck,
} from "lucide-react";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import MfaPersonalManager from "../MfaPersonalManager";

const getInitialFormData = () => ({
  name: "",
  email: "",
  cpf: "",
  password: "",
  profileImage: "",
});

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: "24px",
      width: "min(780px, calc(100vw - 32px))",
      maxWidth: "calc(100vw - 32px)",
      background:
        theme.mode === "dark"
          ? "linear-gradient(135deg, rgba(18, 18, 19, 0.98) 0%, rgba(22, 22, 24, 0.98) 100%)"
          : "#FFFFFF",
      boxShadow:
        theme.mode === "dark"
          ? "0 8px 24px rgba(0, 0, 0, 0.5)"
          : "0 8px 24px rgba(0, 0, 0, 0.1)",
      border: theme.mode === "dark" ? "1px solid rgba(91, 124, 255, 0.2)" : "none",
      [theme.breakpoints.down("sm")]: {
        minWidth: "unset",
        width: "calc(100% - 16px)",
        margin: 8,
        borderRadius: "16px",
      },
    },
    "& .MuiBackdrop-root": {
      backgroundColor:
        theme.mode === "dark" ? "rgba(0, 0, 0, 0.92)" : "rgba(15, 23, 42, 0.6)",
    },
  },
  dialogTitle: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom:
      theme.mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    "& .MuiTypography-root": {
      fontSize: "1.35rem",
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: theme.mode === "dark" ? "#f8fafc" : "#0f172a",
    },
  },
  dialogContent: {
    padding: theme.spacing(3),
  },

  /* ── Abas ── */
  tabBar: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  tabBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    height: 42,
    padding: "0 18px",
    borderRadius: 11,
    border: "1px solid transparent",
    background: "transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.875rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(15, 23, 42, 0.5)",
    transition: "color .18s ease, background .18s ease, border-color .18s ease",
    "& svg": { flexShrink: 0, opacity: 0.85 },
    "&:hover": {
      color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.85)" : "rgba(15, 23, 42, 0.8)",
      background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(15, 23, 42, 0.03)",
    },
    [theme.breakpoints.down("xs")]: { flex: 1, padding: "0 10px", gap: 6 },
  },
  tabBtnActive: {
    color: theme.mode === "dark" ? "#ffffff" : "#0f172a",
    background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.025)",
    border:
      theme.mode === "dark"
        ? "1px solid rgba(91, 124, 255, 0.65)"
        : "1px solid rgba(61, 92, 253, 0.55)",
    "& svg": { opacity: 1, color: theme.mode === "dark" ? "#7c9bff" : "#3D5CFD" },
    "&:hover": {
      color: theme.mode === "dark" ? "#ffffff" : "#0f172a",
      background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.055)" : "rgba(15, 23, 42, 0.03)",
    },
  },
  tabLabelXs: {
    [theme.breakpoints.down("xs")]: { display: "none" },
  },
  "@keyframes panelFade": {
    from: { opacity: 0, transform: "translateY(5px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  tabPanel: {
    animation: "$panelFade 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
    minHeight: 296,
  },

  /* ── Campos ── */
  formField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      background:
        theme.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      border:
        theme.mode === "dark"
          ? "1px solid rgba(91, 124, 255, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.08)",
      transition: "background-color 0.2s ease, border-color 0.2s ease",
      "& fieldset": {
        borderColor: "transparent",
      },
      "&:hover": {
        background:
          theme.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(61, 92, 253, 0.04)",
        border:
          theme.mode === "dark"
            ? "1px solid rgba(91, 124, 255, 0.25)"
            : "1px solid rgba(61, 92, 253, 0.15)",
      },
      "&.Mui-focused": {
        background:
          theme.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 1)",
        border:
          theme.mode === "dark"
            ? "1px solid rgba(91, 124, 255, 0.4)"
            : "1px solid rgba(61, 92, 253, 0.3)",
        boxShadow: "0 0 0 2px rgba(91, 124, 255, 0.25)",
      },
    },
    "& .MuiOutlinedInput-input": {
      paddingTop: 12,
      paddingBottom: 12,
    },
    "& .MuiInputLabel-outlined": {
      transform: "translate(14px, 14px) scale(1)",
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
  },
  row2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  formControlTight: {
    marginBottom: 0,
    width: "100%",
  },

  /* ── Identidade / avatar ── */
  identityRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2.5),
  },
  avatarUpload: {
    position: "relative",
    width: 84,
    height: 84,
    borderRadius: "50%",
    overflow: "hidden",
    cursor: "pointer",
    flexShrink: 0,
    border:
      theme.mode === "dark"
        ? "1px solid rgba(91, 124, 255, 0.3)"
        : "1px solid rgba(0, 0, 0, 0.08)",
    "&:hover $avatarOverlay": { opacity: 1 },
  },
  identityAvatar: {
    width: 84,
    height: 84,
    fontSize: 30,
    fontWeight: 700,
    background: theme.mode === "dark" ? "rgba(91, 124, 255, 0.16)" : "rgba(61, 92, 253, 0.08)",
    color: theme.mode === "dark" ? "#9db4ff" : "#3D5CFD",
  },
  avatarOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    background: "rgba(10, 12, 20, 0.55)",
    color: "#ffffff",
    opacity: 0,
    transition: "opacity .2s ease",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  identityFields: { flex: 1, minWidth: 0 },
  identityHint: {
    display: "block",
    marginTop: 6,
    fontSize: 11.5,
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.4)" : "#94a3b8",
  },

  loadingState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 296,
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "#64748b",
    fontSize: "0.9rem",
  },

  /* ── Footer ── */
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.06)"
      : "1px solid rgba(15, 23, 42, 0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  btnBase: {
    height: 38,
    padding: "0 16px",
    borderRadius: 10,
    fontSize: "0.875rem",
    fontWeight: 500,
    textTransform: "none",
    whiteSpace: "nowrap",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: "none",
    boxShadow: "none",
    transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
    "& svg": { flexShrink: 0 },
    "& .MuiButton-label": { whiteSpace: "nowrap" },
  },
  btnCancel: {
    background: "transparent",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.12)"
      : "1px solid rgba(15, 23, 42, 0.12)",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
    "&:hover": {
      background: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
      color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    },
  },
  btnPrimary: {
    background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
    color: "#ffffff",
    "&:hover": {
      background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
    },
    "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
  },
  spinner: { color: "#ffffff" },
}));

const UserModal = ({ open, onClose, userId }) => {
  const classes = useStyles();

  const [formData, setFormData] = useState(getInitialFormData());
  const [imagePreview, setImagePreview] = useState("");
  const [jobRoleName, setJobRoleName] = useState("");
  const [activeTab, setActiveTab] = useState("dados");
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);

  const updateFormData = (patch) => {
    setFormData((prevState) => ({
      ...prevState,
      ...patch,
    }));
  };

  const resetState = () => {
    setFormData(getInitialFormData());
    setImagePreview("");
    setJobRoleName("");
    setActiveTab("dados");
    setSaving(false);
    setLoadingData(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    if (!open || !userId) {
      return undefined;
    }

    let isMounted = true;

    const loadUser = async () => {
      setLoadingData(true);
      try {
        const { data } = await api.get(`/users/${userId}`);
        if (!isMounted) return;
        setFormData({
          ...getInitialFormData(),
          name: data.name || "",
          email: data.email || "",
          cpf: data.cpf || "",
          profileImage: data.profileImage || "",
        });
        setImagePreview(data.profileImage || "");
        setJobRoleName(data.jobRole?.name || "");
      } catch (err) {
        if (isMounted) toastError(err);
      } finally {
        if (isMounted) setLoadingData(false);
      }
    };

    loadUser();

    return () => {
      isMounted = false;
    };
  }, [open, userId]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      updateFormData({ profileImage: base64String });
      setImagePreview(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Só campos pessoais — o whitelist de self-edit do backend é a fonte da
      // verdade; nada administrativo (filas/conexão/perfil) é enviado.
      const payload = {
        name: formData.name,
        email: formData.email,
        cpf: formData.cpf,
        password: formData.password,
        profileImage: formData.profileImage,
      };
      await api.put(`/users/${userId}`, payload);
      toast.success(i18n.t("users.toasts.updated"));
      handleClose();
    } catch (err) {
      toastError(err);
      setSaving(false);
    }
  };

  const tabs = [
    { key: "dados", label: "Dados", icon: PersonIcon },
    { key: "seguranca", label: "Segurança", icon: ShieldCheck },
  ];

  return (
    <Dialog open={open} onClose={handleClose} className={classes.dialog}>
      <DialogTitle className={classes.dialogTitle}>Meu Perfil</DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loadingData ? (
          <div className={classes.loadingState}>
            <CircularProgress size={22} />
          </div>
        ) : (
          <>
            {/* Abas */}
            <div className={classes.tabBar} role="tablist">
              {tabs.map((tabItem) => {
                const TabIcon = tabItem.icon;
                const active = activeTab === tabItem.key;
                return (
                  <button
                    key={tabItem.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`${classes.tabBtn} ${active ? classes.tabBtnActive : ""}`}
                    onClick={() => setActiveTab(tabItem.key)}
                  >
                    <TabIcon size={16} strokeWidth={active ? 1.9 : 1.75} />
                    <span className={classes.tabLabelXs}>{tabItem.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Input de upload (usado pelo avatar da aba Dados) */}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
              id="profile-modal-image-upload"
            />

            <div className={classes.tabPanel} key={activeTab}>
              {/* ───────── DADOS ───────── */}
              {activeTab === "dados" && (
                <>
                  <div className={classes.identityRow}>
                    <label
                      htmlFor="profile-modal-image-upload"
                      className={classes.avatarUpload}
                    >
                      <Avatar
                        className={classes.identityAvatar}
                        src={imagePreview || undefined}
                        alt=""
                      >
                        {(formData.name || "?").trim().charAt(0).toUpperCase()}
                      </Avatar>
                      <span className={classes.avatarOverlay}>
                        <Upload size={16} strokeWidth={2} />
                        {imagePreview ? "Alterar" : "Foto"}
                      </span>
                    </label>
                    <div className={classes.identityFields}>
                      <FormControl className={classes.formControlTight}>
                        <TextField
                          label={i18n.t("userModal.form.name")}
                          variant="outlined"
                          value={formData.name}
                          onChange={(event) => updateFormData({ name: event.target.value })}
                          fullWidth
                          className={classes.formField}
                        />
                      </FormControl>
                      <span className={classes.identityHint}>
                        Clique no avatar para enviar uma foto (opcional).
                      </span>
                    </div>
                  </div>

                  <div className={classes.row2}>
                    <FormControl className={classes.formControlTight}>
                      <TextField
                        label={i18n.t("userModal.form.email")}
                        variant="outlined"
                        type="email"
                        value={formData.email}
                        onChange={(event) => updateFormData({ email: event.target.value })}
                        fullWidth
                        className={classes.formField}
                      />
                    </FormControl>
                    <FormControl className={classes.formControlTight}>
                      <InputMask
                        mask="999.999.999-99"
                        maskChar={null}
                        value={formData.cpf}
                        onChange={(event) => updateFormData({ cpf: event.target.value })}
                      >
                        {(inputProps) => (
                          <TextField
                            {...inputProps}
                            label={i18n.t("userModal.form.cpf")}
                            variant="outlined"
                            fullWidth
                            className={classes.formField}
                          />
                        )}
                      </InputMask>
                    </FormControl>
                  </div>

                  <div className={classes.row2}>
                    <FormControl className={classes.formControlTight}>
                      <TextField
                        label={i18n.t("userModal.form.password")}
                        variant="outlined"
                        type="password"
                        placeholder="Deixe em branco para não alterar"
                        value={formData.password}
                        onChange={(event) => updateFormData({ password: event.target.value })}
                        fullWidth
                        className={classes.formField}
                      />
                    </FormControl>
                    <FormControl className={classes.formControlTight}>
                      <TextField
                        label={i18n.t("userModal.form.jobRole")}
                        variant="outlined"
                        value={jobRoleName || i18n.t("userModal.form.noJobRole")}
                        disabled
                        fullWidth
                        className={classes.formField}
                        helperText="Definido pelo administrador, supervisor ou gerente"
                      />
                    </FormControl>
                  </div>
                </>
              )}

              {/* ───────── SEGURANÇA ───────── */}
              {activeTab === "seguranca" && <MfaPersonalManager />}
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleClose}
          disabled={saving}
          className={`${classes.btnBase} ${classes.btnCancel}`}
          startIcon={<X size={16} strokeWidth={1.75} />}
        >
          {i18n.t("userModal.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loadingData}
          className={`${classes.btnBase} ${classes.btnPrimary}`}
          startIcon={
            saving ? (
              <CircularProgress size={14} className={classes.spinner} />
            ) : (
              <Pencil size={16} strokeWidth={1.75} />
            )
          }
        >
          {i18n.t("userModal.buttons.okEdit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserModal;
